import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchTemplates,
  updateSelectedTemplate,
} from "../../../../redux/Slice/Templates/templateSlice";

import {
  Box,
  Typography,
  Snackbar,
  Alert,
  Skeleton,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { DiscountShape, DocumentText1, ShieldTick } from "iconsax-react";
import { slimScrollbarSx } from "../../../../utils/scrollbarStyles";

// Real WhatsApp template categories (Meta Graph API — template.category is
// always one of these 3) — each gets its own icon+color square, like every
// other row-icon convention in the app (Rahim, 2026-08-24: "give the template
// sections there own icons depending upon the template").
const CATEGORY_ICON = {
  MARKETING: { Icon: DiscountShape, color: "warning", label: "Marketing" },
  UTILITY: { Icon: DocumentText1, color: "primary", label: "Utility" },
  AUTHENTICATION: { Icon: ShieldTick, color: "info", label: "Authentication" },
};

// Neutral pill, same shape as the lifecycle-stage chip's own "outline"
// variant (LifecycleStageChip.jsx: borderRadius "6px", small flat padding) —
// Rahim, 2026-08-24: "adjust the Type in a pill like we did here [the 'new'
// stage chip]... right beside the name at the most right section".
const TemplateTypePill = ({ category }) => {
  const { label } = CATEGORY_ICON[category] || CATEGORY_ICON.UTILITY;
  return (
    <Box
      component="span"
      sx={{
        flexShrink: 0,
        borderRadius: "6px",
        px: 1,
        py: 0.25,
        fontSize: "0.6875rem",
        fontWeight: 600,
        color: "text.secondary",
        bgcolor: (t) => (t.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"),
      }}
    >
      {label}
    </Box>
  );
};

// Now matches ChatInfoRail.jsx's rail icon buttons for real (not just the
// corner radius) — bordered outline, transparent fill, colored glyph (Rahim,
// 2026-08-24: "try border colour black and transparent colour icon like we
// did for the right rail"). Transparent background also sidesteps the whole
// hover-merge problem the last 3 attempts kept running into — there's no fill
// left to blend with the row's hover tint, so the row's own background just
// shows through evenly everywhere, corners included, same as it does around
// every other transparent element in these rows.
const TemplateRowIcon = ({ category }) => {
  const theme = useTheme();
  const { Icon, color } = CATEGORY_ICON[category] || CATEGORY_ICON.UTILITY;
  const paletteColor = theme.palette[color].main;
  return (
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: "10px",
        border: `1px solid ${theme.palette.divider}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <Icon size="18" variant="Bold" color={paletteColor} />
    </Box>
  );
};

function AllTemplates({ searchQuery = "", onTemplateSelected }) {
  const dispatch = useDispatch();
  const { templates, status, error, selectedTemplate } = useSelector(
    (state) => state.template
  );

  // For error display
  const [openSnackbar, setOpenSnackbar] = useState(false);

  useEffect(() => {
    dispatch(fetchTemplates());
  }, [dispatch]);

  // Whenever there's an error, open the Snackbar
  useEffect(() => {
    if (error) {
      setOpenSnackbar(true);
    }
  }, [error]);

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  // Filter only APPROVED & match search text
  const approvedTemplates = templates
    .filter((t) => t.status === "APPROVED")
    .filter((t) => t.name.toLowerCase().includes(searchQuery.toLowerCase()));

  // Default to first approved if none selected
  useEffect(() => {
    if (!selectedTemplate && approvedTemplates.length > 0) {
      dispatch(updateSelectedTemplate(approvedTemplates[0]));
    }
  }, [approvedTemplates, selectedTemplate, dispatch]);

  if (status === "loading") {
    return (
      <Box sx={{ padding: { xs: 1.25, sm: 2 } }}>
        {[...Array(5)].map((_, idx) => (
          <Skeleton
            animation="wave"
            key={idx}
            variant="rounded"
            height={36}
            sx={{ mb: { xs: 1.25, sm: 2 } }}
          />
        ))}
      </Box>
    );
  }

  const handleMouseEnter = (template) => {
    dispatch(updateSelectedTemplate(template));
  };

  /**
   * 1) Update Redux with the clicked template
   * 2) Call the parent's callback to open the second dialog
   */
  const handleTemplateClick = (template) => {
    dispatch(updateSelectedTemplate(template));
    onTemplateSelected(template);
  };

  return (
    <Box sx={{ overflowY: "auto", overflowX: "hidden", height: "100%", ...slimScrollbarSx }}>
      <List sx={{ padding: 0 }}>
        {approvedTemplates.map((template, index) => {
          const bodyComponent = template.components?.find(
            (c) => c.type?.toUpperCase() === "BODY"
          );
          const isSelected =
            selectedTemplate && selectedTemplate.name === template.name;

          return (
            <ListItem
              key={template.name + index}
              onMouseEnter={() => handleMouseEnter(template)}
              onClick={() => handleTemplateClick(template)}
              sx={(theme) => ({
                // Same rounded-pill/inset row treatment as InboxSettings.jsx's
                // filter rail and the composer popups' rows (Rahim, 2026-08-24:
                // "for hover and clicked use our own new style... better
                // spacing and gaps") — was a flat full-bleed row using MUI's
                // plain action.selected/action.hover tokens.
                alignItems: "flex-start",
                cursor: "pointer",
                borderRadius: "8px",
                // Forces the hover/selected background to actually clip to
                // the rounded corners — without this the fill painted square
                // regardless of borderRadius (Rahim, 2026-08-24, screenshot:
                // "still cornered pointed corner not rounded one").
                overflow: "hidden",
                transition: "background-color 0.12s ease",
                // Bumped again — "12px" (itself bumped from "8px 10px" last
                // round) still read short (Rahim, 2026-08-24: "make each
                // card height a little bigger").
                padding: "14px 12px",
                // This app has no global box-sizing:border-box reset (see
                // ui-gotchas.md) — without this, the row's implicit
                // width:100% + mx:1 (16px) added together under content-box
                // sizing instead of the margin being eaten out of the 100%,
                // overflowing 8px past the right edge. Since the parent only
                // sets overflowY (no overflowX), that overflow silently
                // clipped the row's right margin/rounded-corner instead of
                // showing — the exact "no right gap, flush corner" bug
                // (Rahim, 2026-08-24, screenshot).
                boxSizing: "border-box",
                mx: 1,
                mt: index === 0 ? 1 : 0.5,
                mb: index === approvedTemplates.length - 1 ? 1 : 0,
                backgroundColor: isSelected
                  ? theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"
                  : "transparent",
                "&:hover": {
                  backgroundColor: isSelected
                    ? theme.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.09)"
                    : theme.palette.mode === "dark" ? "rgba(255,255,255,0.055)" : "rgba(0,0,0,0.045)",
                },
                gap: 1.25,
              })}
            >
              <TemplateRowIcon category={template.category} />
              <ListItemText
                sx={{ my: 0 }}
                primary={
                  // Body Emphasis token (0.9375rem/500) — the same row-title
                  // size/weight used everywhere else in Inbox (MessageCard's
                  // contact name, ContactRow, TeamMemberRow — see
                  // font-sizing-scale.md). Was subtitle1/bold, responsively
                  // bumping up to 1rem — heavier and bigger than the rest of
                  // the app's row titles (Rahim, 2026-08-24: "use better font
                  // sizing and weight as done inside the overall inbox page").
                  // mb here is the actual fix — the heading and preview text
                  // had zero gap between them (both default to margin:0), so
                  // they sat touching (Rahim, 2026-08-24: "the heading and
                  // the subpara doesnt have proper gap between them"). Type
                  // pill now sits on this same row, pushed to the far right
                  // via ml:"auto".
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                    <Typography
                      sx={{
                        fontSize: "0.9375rem",
                        fontWeight: 500,
                        lineHeight: 1.25,
                        color: "text.primary",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        minWidth: 0,
                      }}
                    >
                      {template.name}
                    </Typography>
                    <Box sx={{ ml: "auto" }}>
                      <TemplateTypePill category={template.category} />
                    </Box>
                  </Box>
                }
                secondary={
                  bodyComponent ? bodyComponent.text : "No body text found."
                }
                secondaryTypographyProps={{
                  sx: {
                    // Small/Label token (0.8125rem), flat at every breakpoint —
                    // matches MessageCard's preview-text size exactly.
                    fontSize: "0.8125rem",
                    lineHeight: 1.35,
                    display: "-webkit-box",
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    // Was clamped with no ellipsis marker — long preview text
                    // just cut off mid-word with no visual "there's more"
                    // signal (Rahim, 2026-08-24: "for long sub paras use ...").
                    textOverflow: "ellipsis",
                  },
                }}
              />
            </ListItem>
          );
        })}
      </List>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="error"
          sx={{ width: "100%" }}
        >
          {error || "An error occurred while fetching templates."}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default AllTemplates;
